import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { Plugin } from "@/types";

// #290 (UC-4): Der Katalog ist mit einem echten Marketplace sofort dreistellig (286 Einträge
// bei claude-plugins-official) — ohne Suche/Filter/Deckel ist die Liste eine 20'000-Pixel-
// Wand. EINE Filter-Logik + EINE Toolbar für beide Katalog-Tabellen (aggregiert /plugins +
// Marketplace-Detail); zwei Kopien wären zwei driftende Regeln.

export type PluginFilterKind = "alle" | "freigegeben" | "gesperrt" | "auffaellig";

export const KATALOG_SEITE = 50;

// Pur + exportiert (testbar ohne Rendern). „auffällig" = verwaist ODER Quelle nicht lesbar —
// genau die Einträge, die Aufmerksamkeit brauchen.
export function filterPlugins(plugins: readonly Plugin[], q: string, kind: PluginFilterKind): Plugin[] {
  const needle = q.trim().toLowerCase();
  return plugins.filter((p) => {
    if (kind === "freigegeben" && !p.approved) return false;
    if (kind === "gesperrt" && p.approved) return false;
    if (kind === "auffaellig" && p.status === "available") return false;
    if (!needle) return true;
    return (
      p.name.toLowerCase().includes(needle) ||
      p.description.toLowerCase().includes(needle) ||
      p.marketplaceName.toLowerCase().includes(needle)
    );
  });
}

// Hook: Filter-Zustand + gefilterte/gedeckelte Sicht. Der Deckel resettet bei jeder
// Filter-Änderung (sonst zeigte „Mehr anzeigen"-Zustand A die Treffer von Filter B an).
export function usePluginFilter(plugins: readonly Plugin[]) {
  const [q, setQ] = useState("");
  const [kind, setKind] = useState<PluginFilterKind>("alle");
  const [limit, setLimit] = useState(KATALOG_SEITE);

  const gefiltert = useMemo(() => filterPlugins(plugins, q, kind), [plugins, q, kind]);
  const sichtbar = gefiltert.slice(0, limit);

  return {
    q,
    kind,
    sichtbar,
    // Review-Fix E: die GEFILTERTE Gesamtmenge (ungedeckelt) — z.B. für die
    // BulkApproveBar: sie muss über dieselbe Menge sprechen, die die Tabelle zeigt,
    // sonst steht „286 nicht freigeschaltet" über einer 1-Treffer-Liste.
    alleGefiltert: gefiltert,
    gesamt: gefiltert.length,
    verdeckt: Math.max(0, gefiltert.length - limit),
    setQ: (v: string) => {
      setQ(v);
      setLimit(KATALOG_SEITE);
    },
    setKind: (v: PluginFilterKind) => {
      setKind(v);
      setLimit(KATALOG_SEITE);
    },
    mehr: () => setLimit((l) => l + KATALOG_SEITE),
  };
}

export function PluginCatalogToolbar({ filter }: { filter: ReturnType<typeof usePluginFilter> }) {
  return (
    <div className="mb-3 flex items-center gap-2">
      <div className="relative flex-1">
        <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text3" />
        <input
          value={filter.q}
          onChange={(e) => filter.setQ(e.target.value)}
          placeholder="Plugin suchen (Name, Beschreibung, Quelle)…"
          className="h-8 w-full rounded-lg border border-border-strong bg-surface pl-8 pr-2 text-[12.5px] text-text outline-none focus:border-accent-border"
          data-testid="catalog-search"
        />
      </div>
      <Select value={filter.kind} onValueChange={(v) => filter.setKind(v as PluginFilterKind)}>
        <SelectTrigger className="h-8 w-[190px] text-[12px]" data-testid="catalog-filter">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="alle">Alle</SelectItem>
          <SelectItem value="freigegeben">Freigeschaltet</SelectItem>
          <SelectItem value="gesperrt">Nicht freigeschaltet</SelectItem>
          <SelectItem value="auffaellig">Verwaist / Quelle gestört</SelectItem>
        </SelectContent>
      </Select>
      <span className="shrink-0 text-[12px] text-text3" data-testid="catalog-count">
        {filter.sichtbar.length} von {filter.gesamt}
      </span>
    </div>
  );
}

// Unter der Tabelle: der Rest ist DA, nur nicht gerendert — der Knopf sagt, wie viel.
export function PluginCatalogMore({ filter }: { filter: ReturnType<typeof usePluginFilter> }) {
  if (filter.verdeckt <= 0) return null;
  return (
    <div className="mt-2 flex justify-center">
      <Button variant="outline" size="sm" onClick={filter.mehr} data-testid="catalog-more">
        {filter.verdeckt} weitere anzeigen
      </Button>
    </div>
  );
}
